if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.lazyInitRequested = false;
      this.autoplayTimer = null;
      this.setActiveSlideTimeoutId = null;
      this.wheelUnlockTimeoutId = null;
      this.intersectionObserver = null;
      this.pauseReasons = new Set();
      this.userPaused = false;

      this.handleNavClickBound = this.handleNavClick.bind(this);
      this.handlePaginationClickBound = this.handlePaginationClick.bind(this);
      this.handleWheelBound = this.handleWheel.bind(this);
      this.handleTouchStartBound = this.handleTouchStart.bind(this);
      this.handleTouchEndBound = this.handleTouchEnd.bind(this);
      this.handleMouseEnterBound = () => this.pauseAutoplay('pointer');
      this.handleMouseLeaveBound = () => this.resumeAutoplay('pointer');
      this.handleFocusInBound = () => this.pauseAutoplay('focus');
      this.handleFocusOutBound = this.handleFocusOut.bind(this);
      this.handleVisibilityChangeBound = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized || this.lazyInitRequested) return;

      this.lazyInitRequested = true;
      const init = () => {
        this.lazyInitRequested = false;
        if (this.isConnected) this.init();
      };

      if (window.Shopify && Shopify.designMode) {
        init();
      } else if (window.initLazyScript) {
        window.initLazyScript(this, init);
      } else {
        init();
      }
    }

    disconnectedCallback() {
      this.clearAutoplayTimer();
      clearTimeout(this.setActiveSlideTimeoutId);
      clearTimeout(this.wheelUnlockTimeoutId);

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.nav) this.nav.removeEventListener('click', this.handleNavClickBound);
      if (this.pagination) this.pagination.removeEventListener('click', this.handlePaginationClickBound);
      if (this.slideshow) {
        this.slideshow.removeEventListener('wheel', this.handleWheelBound);
        this.slideshow.removeEventListener('touchstart', this.handleTouchStartBound);
        this.slideshow.removeEventListener('touchend', this.handleTouchEndBound);
        this.slideshow.removeEventListener('transitionend', SlideShow.handleTransitionend);
      }

      this.removeEventListener('mouseenter', this.handleMouseEnterBound);
      this.removeEventListener('mouseleave', this.handleMouseLeaveBound);
      this.removeEventListener('focusin', this.handleFocusInBound);
      this.removeEventListener('focusout', this.handleFocusOutBound);
      document.removeEventListener('visibilitychange', this.handleVisibilityChangeBound);

      this.initialized = false;
      this.lazyInitRequested = false;
    }

    init() {
      if (this.initialized) return;

      this.slideshow = this.querySelector('.slideshow');
      this.slides = this.querySelectorAll('.slideshow__slide');
      if (!this.slideshow || this.slides.length === 0) return;

      this.initialized = true;
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.pageBtns = this.querySelectorAll('.page-btn');
      this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
      this.autoplayBtn = this.querySelector('.autoplay-btn');
      this.rtl = document.dir === 'rtl';
      this.swipeThreshold = 80;
      this.wheelTriggerWait = 800;
      this.blockWheelTrigger = false;
      this.touchStart = null;

      const activeSlideIndex = Array.from(this.slides).findIndex((slide) => slide.classList.contains('is-active'));
      this.currentIndex = activeSlideIndex >= 0 ? activeSlideIndex : 0;

      if (this.nav) this.nav.addEventListener('click', this.handleNavClickBound);
      if (this.pagination) this.pagination.addEventListener('click', this.handlePaginationClickBound);

      if (theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStartBound, { passive: true });
        this.slideshow.addEventListener('touchend', this.handleTouchEndBound, { passive: true });
      }

      this.slideshow.addEventListener('wheel', this.handleWheelBound, { passive: false });
      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);
      this.setSlideVisibility();
      this.updatePagination();

      this.autoplayConfigured = this.dataset.autoplay === 'true' && this.slides.length > 1;
      if (this.autoplayConfigured) this.initAutoPlay();
    }

    initAutoPlay() {
      const speedInSeconds = Number.parseFloat(this.dataset.speed);
      this.autoplaySpeed = Number.isFinite(speedInSeconds) && speedInSeconds > 0
        ? speedInSeconds * 1000
        : 5000;
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.autoplayDeadline = null;
      this.userPaused = false;
      this.pauseReasons = new Set();

      this.addEventListener('mouseenter', this.handleMouseEnterBound);
      this.addEventListener('mouseleave', this.handleMouseLeaveBound);
      this.addEventListener('focusin', this.handleFocusInBound);
      this.addEventListener('focusout', this.handleFocusOutBound);
      document.addEventListener('visibilitychange', this.handleVisibilityChangeBound);

      if (document.hidden) this.pauseReasons.add('document');
      this.addObserver();
      this.syncAutoplayButton();
      this.syncSlideshowState();
    }

    handleNavClick(evt) {
      const button = evt.target.closest('button');
      if (!button || !this.nav.contains(button)) return;

      if (button.matches('.autoplay-btn')) {
        this.togglePlayState();
      } else if (button.name === 'next') {
        this.showNextSlide();
      } else if (button.name === 'prev') {
        this.showPrevSlide();
      }
    }

    handlePaginationClick(evt) {
      const button = evt.target.closest('.page-btn');
      if (!button || !this.pagination.contains(button)) return;

      this.setActiveSlide(Number(button.dataset.index));
    }

    handleWheel(evt) {
      if (Math.abs(evt.deltaX) <= 20) return;

      evt.preventDefault();
      if (this.blockWheelTrigger) return;

      if (evt.deltaX > 0) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      } else {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }

      this.blockWheelTrigger = true;
      clearTimeout(this.wheelUnlockTimeoutId);
      this.wheelUnlockTimeoutId = setTimeout(() => {
        this.blockWheelTrigger = false;
      }, this.wheelTriggerWait);
    }

    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    handleTouchEnd(evt) {
      if (this.touchStart === null) return;

      const touchEnd = evt.changedTouches[0].screenX;
      const distance = touchEnd - this.touchStart;
      this.touchStart = null;

      if (Math.abs(distance) < this.swipeThreshold) return;

      if (distance < 0) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      } else {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    handleFocusOut(evt) {
      if (evt.relatedTarget && this.contains(evt.relatedTarget)) return;
      this.resumeAutoplay('focus');
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay('document');
      } else {
        this.resumeAutoplay('document');
      }
    }

    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      if (!this.ensureInitialized()) return;

      let index = this.currentIndex + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      if (!this.ensureInitialized()) return;

      let index = this.currentIndex - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    setActiveSlide(slideIndex, slideDirection) {
      if (!this.ensureInitialized()) return;

      const nextIndex = Number(slideIndex);
      if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= this.slides.length) return;
      if (nextIndex === this.currentIndex) {
        this.resetAutoplayCycle();
        return;
      }

      clearTimeout(this.setActiveSlideTimeoutId);
      this.clearAutoplayTimer();
      if (this.autoplayConfigured) this.autoplayTimeLeft = this.autoplaySpeed;

      let direction = slideDirection || (nextIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) direction = direction === 'next' ? 'prev' : 'next';
      this.dataset.direction = direction;

      const previousSlide = this.slides[this.currentIndex];
      previousSlide.classList.add('transition-out');
      this.syncSlideshowState();

      this.setActiveSlideTimeoutId = setTimeout(() => {
        this.slideshow.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior: 'auto' });
        this.slides[nextIndex].classList.add('is-active');
        previousSlide.classList.remove('is-active', 'transition-out');

        const transitionedEl = previousSlide.querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        this.currentIndex = nextIndex;
        if (this.counterCurrent) this.counterCurrent.textContent = nextIndex + 1;
        this.setSlideVisibility();
        this.updatePagination();
        this.resetAutoplayCycle();
      }, 200);
    }

    ensureInitialized() {
      if (!this.initialized) this.init();
      return this.initialized;
    }

    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((button, index) => {
        if (index === this.currentIndex) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    restartPaginationProgress() {
      if (!this.pagination || !this.pageBtns[this.currentIndex]) return;

      const currentButton = this.pageBtns[this.currentIndex];
      currentButton.removeAttribute('aria-current');
      void currentButton.offsetWidth;
      currentButton.setAttribute('aria-current', 'true');
    }

    clearAutoplayTimer() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.autoplayDeadline = null;
    }

    stopAutoplay() {
      this.clearAutoplayTimer();
      this.syncSlideshowState();
    }

    startAutoplay(reset = false) {
      if (!this.autoplayConfigured) return;
      if (reset) this.autoplayTimeLeft = this.autoplaySpeed;
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      this.clearAutoplayTimer();
      if (!this.autoplayConfigured || this.pauseReasons.size > 0 || !this.isConnected) {
        this.syncSlideshowState();
        return;
      }

      const delay = Math.max(100, Math.min(this.autoplayTimeLeft, this.autoplaySpeed));
      this.autoplayTimeLeft = delay;
      this.autoplayDeadline = performance.now() + delay;
      this.autoplayTimer = setTimeout(() => {
        this.autoplayTimer = null;
        this.autoplayDeadline = null;
        this.autoplayTimeLeft = this.autoplaySpeed;
        this.showNextSlide();
      }, delay);
      this.syncSlideshowState();
    }

    resetAutoplayCycle() {
      if (!this.autoplayConfigured) return;

      this.autoplayTimeLeft = this.autoplaySpeed;
      this.restartPaginationProgress();
      this.scheduleAutoplay();
    }

    pauseAutoplay(reason = 'external') {
      if (!this.ensureInitialized() || !this.autoplayConfigured) return;

      this.pauseReasons.add(reason);
      if (this.autoplayTimer && this.autoplayDeadline !== null) {
        this.autoplayTimeLeft = Math.max(0, this.autoplayDeadline - performance.now());
      }
      this.clearAutoplayTimer();
      this.syncSlideshowState();
    }

    resumeAutoplay(reason = 'external') {
      if (!this.ensureInitialized() || !this.autoplayConfigured) return;

      this.pauseReasons.delete(reason);
      this.scheduleAutoplay();
    }

    addObserver() {
      if (!('IntersectionObserver' in window)) {
        this.scheduleAutoplay();
        return;
      }

      this.pauseReasons.add('viewport');
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== this) return;
          if (entry.isIntersecting) {
            this.resumeAutoplay('viewport');
          } else {
            this.pauseAutoplay('viewport');
          }
        });
      }, { threshold: 0.01 });
      this.intersectionObserver.observe(this);
    }

    setSlideVisibility() {
      this.slides.forEach((slide, index) => {
        const links = slide.querySelectorAll('a');
        if (index === this.currentIndex) {
          slide.setAttribute('aria-hidden', 'false');
          slide.removeAttribute('tabindex');
          links.forEach((link) => link.removeAttribute('tabindex'));
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('tabindex', '-1');
          links.forEach((link) => link.setAttribute('tabindex', '-1'));
        }
      });
    }

    syncSlideshowState() {
      if (!this.slideshow) return;

      const isRunning = Boolean(this.autoplayTimer) && this.pauseReasons.size === 0;
      this.style.setProperty('--play-state', isRunning ? 'running' : 'paused');
      this.slideshow.setAttribute('aria-live', isRunning ? 'off' : 'polite');
      this.syncAutoplayButton();
    }

    syncAutoplayButton() {
      if (!this.autoplayBtn) return;

      this.autoplayBtn.classList.toggle('is-paused', this.userPaused);
      const label = this.userPaused ? this.autoplayBtn.dataset.labelPlay : this.autoplayBtn.dataset.labelPause;
      if (label) this.autoplayBtn.setAttribute('aria-label', label);
    }

    togglePlayState() {
      if (!this.autoplayConfigured) return;

      this.userPaused = !this.userPaused;
      if (this.userPaused) {
        this.pauseAutoplay('user');
      } else {
        this.autoplayTimeLeft = this.autoplaySpeed;
        this.restartPaginationProgress();
        this.resumeAutoplay('user');
      }
      this.syncAutoplayButton();
    }
  }

  customElements.define('slide-show', SlideShow);
}
